import type { HardwareAccelerationKind } from '../webrtcLatencyContracts';

export type VideoPlatform = 'desktop-chromium' | 'android-chromium' | 'safari' | 'firefox' | 'unknown';
export type VideoCaptureMode = 'display' | 'window' | 'camera' | 'unknown';
export type VideoQualityCodec = 'h264' | 'hevc' | 'unknown';

export interface VideoQualityInput {
  platform: VideoPlatform;
  codec: VideoQualityCodec;
  encoder: HardwareAccelerationKind;
  captureMode: VideoCaptureMode;
}

export interface VideoQualityDecision {
  degradationPreference: 'maintain-framerate' | 'maintain-resolution';
  headroomFactor: number;
}

/** Produces sender cadence and headroom decisions from explicit runtime evidence. */
export class VideoQualityPolicy {
  /** Chromium-derived senders preserve cadence so control motion remains responsive. */
  public decide(input: VideoQualityInput): VideoQualityDecision {
    return {
      degradationPreference: this.degradationPreferenceFor(input.platform),
      headroomFactor: this.headroomFactorFor(input),
    };
  }

  /** Return the degradation preference for a browser platform. */
  public degradationPreferenceFor(platform: VideoPlatform): VideoQualityDecision['degradationPreference'] {
    return platform === 'desktop-chromium' || platform === 'android-chromium'
      ? 'maintain-framerate'
      : 'maintain-resolution';
  }

  /** Return bounded scene-change headroom for this platform, codec, encoder, and capture mode. */
  public headroomFactorFor(input: VideoQualityInput): number {
    const codecBase: Record<VideoQualityCodec, number> = {
      h264: 1.8,
      hevc: 1.55,
      unknown: 1.4,
    };
    const platformAdjustment: Record<VideoPlatform, number> = {
      'desktop-chromium': 0.35,
      'android-chromium': 0.1,
      safari: 0.05,
      firefox: 0,
      unknown: 0,
    };
    const encoderAdjustment = input.encoder === 'hardware' ? 0.2 : input.encoder === 'software' ? -0.25 : 0;
    const captureAdjustment = input.captureMode === 'display' || input.captureMode === 'window' ? 0.15 : 0;
    return Math.max(1.1, Math.min(2.5, codecBase[input.codec] + platformAdjustment[input.platform]
      + encoderAdjustment + captureAdjustment));
  }
}

export const videoQualityPolicy = new VideoQualityPolicy();
